import {
  apolloClient,
  fetchBanners,
  fetchMarketById,
  fetchPage,
  fetchPageLinks,
  GetFaqsQuery,
  GetFaqsQueryVariables,
  getTranslation,
  GET_FAQS_QUERY,
  localeToMarketCode,
  PageProps,
  Unarray,
} from '@instabox/consumer-web-shared'
import { Stack } from '@instabox/consumer-web-ui'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import Head from 'next/head'

import ContactUs from '../../components/ContactUs'
import FAQDetails from '../../components/FAQDetails'
import FAQRating from '../../components/FAQRating'
import PageContent from '../../components/PageContent'
import SimilarFAQList from '../../components/SimilarFAQList'
import FAQSearchContainer from '../../containers/FAQSearchContainer'
import useFaqsAndCategories from '../../hooks/useFaqsAndCategories'

export interface FAQPageProps extends PageProps {
  faq: Unarray<GetFaqsQuery['faqs']>
}

const SLUG = 'help'

const FAQPage: NextPage<FAQPageProps> = ({ faq, market, content }) => {
  const { data: { faqs = [], categories = [] } = {} } = useFaqsAndCategories()
  const title = getTranslation('question', faq?.translations, faq?.question)
  const description = getTranslation('answer', faq?.translations, faq?.answer)

  return (
    <>
      <Head>
        {title && (
          <>
            <title>{title}</title>
            <meta property="og:title" content={title} key="og:title" />
          </>
        )}
        {description && (
          <>
            <meta name="description" content={description} key="description" />
            <meta property="og:description" content={description} key="og:description" />
          </>
        )}
      </Head>
      <Stack gap="xl">
        <PageContent content={content} slug={SLUG} />
        <FAQSearchContainer categories={categories} faqs={faqs} />
        <div className="row">
          <div className="col-12 col-lg-8 align-self-start">
            <FAQDetails {...faq}>{market && <FAQRating {...market} />}</FAQDetails>
          </div>
          <div className="col-12 col-lg-4">
            <Stack as="aside">
              <SimilarFAQList faqs={faq.similar_faqs} />
              {market && <ContactUs headingSize="sm" {...market} />}
            </Stack>
          </div>
        </div>
      </Stack>
    </>
  )
}

export default FAQPage

export const getStaticProps: GetStaticProps<FAQPageProps> = async ({
  params: { slug },
  locale,
}) => {
  const translationsFilter = { languages_code: { _eq: locale } }

  const {
    data: { faqs },
  } = await apolloClient.query<GetFaqsQuery, GetFaqsQueryVariables>({
    query: GET_FAQS_QUERY,
    variables: {
      filter: {
        slug: {
          _eq: slug as string,
        },
      },
      translations_filter: translationsFilter,
      faq_categories_translations_filter: translationsFilter,
    },
  })

  const {
    data: { marketById },
  } = await fetchMarketById(localeToMarketCode(locale))

  const page = await fetchPage(locale, SLUG)
  const pageLinks = await fetchPageLinks(locale)
  const banners = await fetchBanners(locale, SLUG)

  return {
    props: {
      faq: faqs[0],
      market: marketById,
      pageLinks,
      banners,
      ...page,
    },
    revalidate: 60,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}
